import React, { useState, useEffect } from 'react';

// Preset colors for static mode (RGB format)
const PRESET_COLORS = [
  [255, 0, 0],     // Red
  [0, 128, 255],   // Blue
  [0, 200, 0],     // Green
  [255, 192, 203], // Pink
  [128, 0, 128],   // Purple
  [255, 165, 0],   // Orange
  [255, 255, 0],   // Yellow
  [0, 255, 255],   // Cyan
  [0, 0, 0],       // Black
  [255, 255, 255]  // White
];

const ColorPalette = ({ colors, isLoading, onGenerateWithColors, colorPickerMode = 'dynamic' }) => {
  const [copiedColor, setCopiedColor] = useState(null);
  const [sortedColors, setSortedColors] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [tempColor, setTempColor] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(null); // For static mode - null means no selection

  // Add debug logging when component mounts
  useEffect(() => {
    console.log('🎨 ColorPalette: Component mounted/updated');
    console.log('🎨 ColorPalette: colors prop:', colors);
    console.log('🎨 ColorPalette: isLoading prop:', isLoading);
    console.log('🎨 ColorPalette: colorPickerMode:', colorPickerMode);
    console.log('🎨 ColorPalette: onGenerateWithColors prop exists:', !!onGenerateWithColors);
  }, [colors, isLoading, onGenerateWithColors, colorPickerMode]);

  // Initialize sorted colors when colors prop changes or when in static mode
  useEffect(() => {
    if (colorPickerMode === 'static') {
      setSortedColors(PRESET_COLORS);
      console.log('🎨 ColorPalette: Using preset colors for static mode');
    } else if (colors && colors.length > 0) {
      setSortedColors(colors.slice(0, 10));
      console.log('🎨 ColorPalette: Setting sortedColors:', colors.slice(0, 10));
    }
  }, [colors, colorPickerMode]);

  // Convert RGB array to hex color
  const rgbToHex = (rgb) => {
    const [r, g, b] = rgb;
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
  };

  // Convert hex to RGB array
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : null;
  };

  // Convert RGB to CSS color string
  const rgbToCss = (rgb) => {
    const [r, g, b] = rgb;
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleColorClick = (color, index) => {
    if (colorPickerMode === 'static') {
      // In static mode, clicking selects the color
      setSelectedColorIndex(index);
    } else {
      // In dynamic mode, clicking opens color picker
      setShowColorPicker(index);
      setTempColor(rgbToHex(color));
    }
  };

  const handleEditSelectedColor = () => {
    if (colorPickerMode === 'static' && selectedColorIndex !== null) {
      setShowColorPicker(selectedColorIndex);
      setTempColor(rgbToHex(sortedColors[selectedColorIndex]));
    }
  };

  const handleCopyColor = (color, index, e) => {
    e.stopPropagation();
    const hexCode = rgbToHex(color);
    navigator.clipboard.writeText(hexCode).then(() => {
      console.log('Color copied to clipboard:', hexCode);
      setCopiedColor(index);
      setTimeout(() => setCopiedColor(null), 1500);
    }).catch(err => {
      console.error('Failed to copy color:', err);
    });
  };

  const handleColorChange = (index, newHexColor) => {
    const newRgb = hexToRgb(newHexColor);
    if (newRgb) {
      const newSortedColors = [...sortedColors];
      newSortedColors[index] = newRgb;
      setSortedColors(newSortedColors);
    }
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(null);
    setTempColor('');
  };

  const handleColorPickerSave = () => {
    if (showColorPicker !== null && tempColor) {
      handleColorChange(showColorPicker, tempColor);
    }
    handleColorPickerClose();
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const newSortedColors = [...sortedColors];
      const draggedColor = newSortedColors[draggedIndex];
      
      // Remove the dragged item
      newSortedColors.splice(draggedIndex, 1);
      
      // Insert it at the new position
      newSortedColors.splice(dropIndex, 0, draggedColor);
      
      setSortedColors(newSortedColors);
    }
    
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleGenerateWithColors = () => {
    console.log('🎨 ColorPalette: handleGenerateWithColors called');
    console.log('🎨 ColorPalette: sortedColors:', sortedColors);
    console.log('🎨 ColorPalette: onGenerateWithColors function exists:', !!onGenerateWithColors);

    if (onGenerateWithColors && sortedColors.length > 0) {
      if (colorPickerMode === 'static') {
        // In static mode, send the selected color if one is selected
        if (selectedColorIndex !== null) {
          const selectedColor = sortedColors[selectedColorIndex];
          console.log('🎨 ColorPalette: Calling onGenerateWithColors with selected color:', selectedColor);
          onGenerateWithColors([selectedColor]);
        } else {
          console.log('🎨 ColorPalette: No color selected, calling with empty array');
          onGenerateWithColors([]);
        }
      } else {
        // In dynamic mode, send all sorted colors
        console.log('🎨 ColorPalette: Calling onGenerateWithColors with colors:', sortedColors);
        onGenerateWithColors(sortedColors);
      }
    } else {
      console.warn('🎨 ColorPalette: Cannot call onGenerateWithColors - missing callback or no colors');
    }
  };

  if (isLoading && colorPickerMode !== 'static') {
    return (
      <div className="color-palette-container">
        <h4 className="text-lg font-semibold mb-3 text-gray-800">Color Palette</h4>
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Extracting colors...</span>
        </div>
      </div>
    );
  }

  if (colorPickerMode !== 'static' && (!colors || colors.length === 0)) {
    return null;
  }

  return (
    <div className="color-palette-container relative">
      <div className="mb-3">
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          {colorPickerMode === 'static' ? 'Change The Color Of Your Logo' : 'Color Palette'}
        </h4>
        {colorPickerMode === 'static' ? (
          <p className="text-sm text-gray-600 font-medium">
            <span className="inline-block">🎨 Select a color for your logo</span>
          </p>
        ) : (
          <p className="text-sm text-gray-600 font-medium">
            <span className="inline-block mr-3">🎨 Click to change colors</span>
            <span className="inline-block">↔️ Drag to reorder</span>
          </p>
        )}
      </div>
      
      <div className="grid grid-cols-5 gap-3 mb-4">
        {sortedColors.map((color, index) => (
          <div
            key={`${color[0]}-${color[1]}-${color[2]}-${index}`}
            className="flex flex-col items-center relative group"
            draggable={colorPickerMode !== 'static'}
            onDragStart={(e) => colorPickerMode !== 'static' && handleDragStart(e, index)}
            onDragOver={colorPickerMode !== 'static' ? handleDragOver : undefined}
            onDragEnter={colorPickerMode !== 'static' ? handleDragEnter : undefined}
            onDrop={(e) => colorPickerMode !== 'static' && handleDrop(e, index)}
            onDragEnd={colorPickerMode !== 'static' ? handleDragEnd : undefined}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Order number - only in dynamic mode */}
            {colorPickerMode !== 'static' && (
              <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold z-10">
                {index + 1}
              </div>
            )}

            {/* Drag handle icon - only in dynamic mode */}
            {colorPickerMode !== 'static' && (
              <div className="absolute -top-2 -right-2 bg-gray-700 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center z-10 cursor-move">
                ⠿
              </div>
            )}

            <div
              className={`relative w-14 h-14 rounded-lg shadow-md cursor-pointer transition-all duration-200 ${
                colorPickerMode === 'static' && selectedColorIndex === index
                  ? 'border-4 border-blue-500 ring-4 ring-blue-300 scale-110'
                  : 'border-2 border-gray-300'
              } ${
                copiedColor === index ? 'ring-2 ring-green-500 scale-105' : ''
              } ${draggedIndex === index ? 'opacity-50 scale-95' : 'hover:scale-110 hover:shadow-xl hover:border-blue-400'}`}
              style={{ backgroundColor: rgbToCss(color) }}
              title={colorPickerMode === 'static' ? 'Click to select this color' : 'Click to change this color'}
              onClick={() => handleColorClick(color, index)}
            >
              {/* Selected checkmark for static mode - positioned at corner to not obscure color */}
              {colorPickerMode === 'static' && selectedColorIndex === index && (
                <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-sm w-6 h-6 rounded-full flex items-center justify-center z-20 pointer-events-none shadow-lg border-2 border-white">
                  <div className="font-bold">✓</div>
                </div>
              )}

              {/* Hover overlay with instructions - only in dynamic mode */}
              {colorPickerMode !== 'static' && hoveredIndex === index && draggedIndex !== index && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-lg flex flex-col items-center justify-center text-white text-xs font-semibold z-20 pointer-events-none">
                  <div className="mb-1">✎ Edit</div>
                  <div className="text-[10px] opacity-90">Click</div>
                </div>
              )}

              {/* Copy button - only in dynamic mode */}
              {colorPickerMode !== 'static' && (
                <button
                  onClick={(e) => handleCopyColor(color, index, e)}
                  className="absolute -bottom-1 -right-1 bg-white text-gray-700 text-xs w-5 h-5 rounded-full flex items-center justify-center shadow-md hover:bg-blue-500 hover:text-white transition-colors z-10"
                  title="Copy hex code"
                >
                  📋
                </button>
              )}
            </div>

            <span className="text-xs text-gray-600 mt-2 font-mono font-semibold">
              {rgbToHex(color)}
            </span>

            {copiedColor === index && (
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow-lg z-30 whitespace-nowrap">
                ✓ Copied!
              </div>
            )}
          </div>
        ))}
      </div>

      {colorPickerMode === 'static' && selectedColorIndex !== null && (
        <div className="mb-3">
          <button
            onClick={handleEditSelectedColor}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg mb-2"
          >
            ✎ Edit Selected Color
          </button>
        </div>
      )}

      <div className="mb-3">
        <button
          onClick={handleGenerateWithColors}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
          onMouseEnter={() => console.log('🎨 ColorPalette: Button hover detected')}
          onMouseDown={() => console.log('🎨 ColorPalette: Button mousedown detected')}
        >
          {colorPickerMode === 'static' ? 'Generate Mockups with Selected Color' : 'Generate Mockups with Color Change'}
        </button>
      </div>

      {colorPickerMode !== 'static' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-800 font-medium">
            💡 <span className="font-semibold">Tip:</span> Click the small copy button (📋) to copy hex codes
          </p>
        </div>
      )}

      {/* Color Picker Modal */}
      {showColorPicker !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Change Color</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Color:
              </label>
              <input
                type="color"
                value={tempColor}
                onChange={(e) => setTempColor(e.target.value)}
                className="w-full h-12 rounded-md border border-gray-300 cursor-pointer"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hex Code:
              </label>
              <input
                type="text"
                value={tempColor}
                onChange={(e) => setTempColor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#000000"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleColorPickerClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleColorPickerSave}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPalette; 